import React, { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'

import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { ESSENTIALS_BASE_URL } from '../../../../../common/constants/appConstants'
import { getSelectedClientId } from '../../../../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../../../../data/store'
import { ESSENTIALS_ACCESS } from '../../../data/essentialsAccess'
import { useEssentialsAccess } from '../../../data/hooks/useEssentialsAccess'
import { getInsertEssentialsArticleForClientId } from '../../../data/messagesSelectors'
import { setEssentialsArticleUrl } from '../../../data/messagesSlice'
import { getAiConversationMixpanelProperties } from '../../../data/mixpanelUtils'

type MessengerWellnessLibrarySideBarProps = {
  insertEssentialsArticleChip?: () => void
  conversationType?: CONVERSATION_TYPE
}
interface EssentialsMessageEvent {
  data: {
    uri?: string
  }
  origin: string
}

const MessengerWellnessLibrarySideBar: React.FC<MessengerWellnessLibrarySideBarProps> = ({
  insertEssentialsArticleChip,
  conversationType,
}) => {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const essentialsAccess = useEssentialsAccess()
  const essentialsRef = useRef<HTMLIFrameElement>(null)
  const essentialsArticleForClientId = useSelector(getInsertEssentialsArticleForClientId)
  const selectedClientId = useSelector(getSelectedClientId)
  const selectedClientIdRef = useRef(selectedClientId)

  // Build iframe src URL with client_id as query parameter
  const essentialsUrl = `${ESSENTIALS_BASE_URL}/search?client_id=${selectedClientId || ''}`

  // Keep ref updated with latest selectedClientId
  useEffect(() => {
    selectedClientIdRef.current = selectedClientId
  }, [selectedClientId])

  // Clear stale article URL when switching clients
  useEffect(() => {
    dispatch(setEssentialsArticleUrl(null))
  }, [dispatch, selectedClientId])

  useEffect(() => {
    // Listen for messages from the iframe
    const handleMessage = (event: EssentialsMessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event?.data?.uri) {
        if (event.data.uri.includes('courses') || event.data.uri.includes('articles')) {
          dispatch(setEssentialsArticleUrl(event.data.uri))
          // Track when provider previews a specific essentials article
          track({
            event: mixpanelEvents.PREVIEW_ESSENTIALS_CONTENT,
            properties: {
              clientId: selectedClientIdRef.current,
              articleUrl: event.data.uri,
              contentType: event.data.uri.includes('courses') ? 'course' : 'article',
              ...getAiConversationMixpanelProperties(conversationType),
            },
          })
        } else {
          dispatch(setEssentialsArticleUrl(null))
        }
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [conversationType, dispatch])

  useEffect(() => {
    if (essentialsArticleForClientId && insertEssentialsArticleChip) {
      insertEssentialsArticleChip()
    }
  }, [essentialsArticleForClientId, insertEssentialsArticleChip])

  if (essentialsAccess === ESSENTIALS_ACCESS.DISABLED) {
    return (
      <InlineWarningBanner
        testId='MessengerWellnessLibrarySideBar-noAccessBanner'
        text={formatMessage({
          defaultMessage: 'This client does not have access to Essentials.',
          description:
            'Warning shown in the Essentials side panel when the client’s employer has Essentials turned off',
        })}
      />
    )
  }

  // Loading the iframe before the customer config resolves renders the raw error response (LCDELIVERY-7927).
  if (essentialsAccess === ESSENTIALS_ACCESS.LOADING) {
    return <LoadingIndicator />
  }

  return (
    <iframe
      // eslint-disable-next-line formatjs/no-literal-string-in-jsx
      title='Essentials'
      ref={essentialsRef}
      src={essentialsUrl}
      width='100%'
      height='100%'
      frameBorder='0'
      scrolling='yes'
    />
  )
}

export default MessengerWellnessLibrarySideBar
